"use client";

import { CaretDownOutlined, CaretUpOutlined, DashOutlined } from "@ant-design/icons";
import cn from "classnames";
import { ReactNode, useCallback, useMemo } from "react";

import { TableColumnOrderDirection, TableColumnType } from "./types";

const TableCaption = ({ children }: { children: ReactNode }) => {
    return <caption>{children}</caption>;
};

const TableHead = ({ children }: { children: ReactNode }) => {
    return <thead className="table-head">{children}</thead>;
};

const TableRow = ({ children }: { children: ReactNode }) => {
    return <tr className="table-row">{children}</tr>;
};

const TableBody = ({ children }: { children: ReactNode }) => {
    return <tbody>{children}</tbody>;
};

const getOrderDirectionIcon = (orderDirection: TableColumnOrderDirection): ReactNode => {
    if (orderDirection) {
        if (orderDirection === "asc") {
            return <CaretUpOutlined />;
        } else {
            return <CaretDownOutlined />;
        }
    } else {
        return <DashOutlined />;
    }
};

const TableHeaderElement = ({
    children,
    orderField,
    orderDirection,
    setOrder,
    width,
    loading = false,
}: {
    orderField?: string;
    orderDirection?: TableColumnOrderDirection;
    children: ReactNode;
    setOrder?: (order: string | null) => void;
    loading?: boolean;
    width?: number | string;
}) => {
    const updateOrder = useCallback(() => {
        if (setOrder) {
            if (orderDirection == "asc") {
                setOrder(`-${orderField}`);
            } else if (orderDirection == "dsc") {
                setOrder(null);
            } else {
                setOrder(orderField as string);
            }
        }
    }, [orderDirection, orderField, setOrder]);

    return (
        <th className="table-head-cell" style={{ width: width }}>
            <div className="w-full h-full flex">
                {children}
                {orderField && (
                    <button disabled={loading} onClick={updateOrder} className="mr-0 ml-auto">
                        {getOrderDirectionIcon(orderDirection ?? null)}
                    </button>
                )}
            </div>
        </th>
    );
};
const TableDataCellElement = ({ children }: { children: ReactNode }) => {
    return <td className="table-cell ">{children}</td>;
};

const Table = ({
    caption,
    columns,
    data,
    rowKey = "id",
    className,
    order,
    setOrder,
    loading = false,
}: {
    caption?: ReactNode;
    columns?: TableColumnType[];
    data?: Record<string, unknown>[];
    rowKey?: string;
    className?: string;
    order?: null | string;
    setOrder?: (orderField: string | null) => void;
    loading?: boolean;
}) => {
    const [tableOrderField, tableOrderDirection] = useMemo<[string | null, TableColumnOrderDirection]>(() => {
        if (order) {
            if (order[0] === "-") {
                return [order.slice(1), "dsc"];
            } else {
                return [order, "asc"];
            }
        } else {
            return [null, null];
        }
    }, [order]);

    return (
        <div className={cn("flex flex-col table-wrapper overflow-x-auto", className)}>
            <table className="table">
                {caption && <TableCaption>{caption}</TableCaption>}
                <TableHead>
                    <TableRow>
                        {(columns ?? []).map((column) => {
                            const key = column.key ?? column.dataIndex;
                            const orderDirection = column.orderField === tableOrderField ? tableOrderDirection : null;

                            return (
                                <TableHeaderElement
                                    loading={loading}
                                    key={key}
                                    width={column.width}
                                    orderDirection={orderDirection}
                                    orderField={column.orderField}
                                    setOrder={setOrder}
                                >
                                    {column.title}
                                </TableHeaderElement>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(data ?? []).map((dataItem) => {
                        const key = String(dataItem[rowKey]);
                        return (
                            <TableRow key={key}>
                                {(columns ?? []).map((column) => {
                                    const key = column.key ?? column.dataIndex;

                                    const value = dataItem[column.dataIndex];
                                    let data: ReactNode = typeof value === "string" || typeof value === "number" ? value : null;
                                    if (column.render) {
                                        data = column.render(value, dataItem);
                                    }

                                    return <TableDataCellElement key={key}>{data}</TableDataCellElement>;
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
                {/* <tfoot>
                <tr>
                    <th scope="row" colSpan="2">
                        Average age
                    </th>
                    <td>33</td>
                </tr>
            </tfoot> */}
            </table>
            {(data ?? []).length == 0 && (
                <div className="flex justify-center align-middle min-h-16 ">
                    <div className="my-auto">Нет данных</div>
                </div>
            )}
        </div>
    );
};

export default Table;
